import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { toast } from "react-toastify";
import { getRightworkAPI } from '../requests/utilities/requests';
import { API_get_learning_swipe_deck_summaries } from '../../../constants';
import LoadSpinner from '../../../utilities/LoadSpinner';

/**
 * Section-editor picker for swipe decks. Mirrors AddQuizToSectionModal:
 * fetches the company's deck summaries on open, lets the admin pick one,
 * and hands the selected deck back via `handleSwipeDeckSelect` so the
 * caller can persist it onto the BlockNote `swipeDeck` block's
 * `swipeDeckSelected` prop.
 */
function AddSwipeDeckToSectionModal(props) {

  const [selectedDeck, setSelectedDeck] = useState("");

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    maxHeight: 800,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  };

  const handleDropdownDeckSelect = (deck) => {
    setSelectedDeck(deck);
  };

  const [isLoading, setIsLoading] = React.useState(false);
  const [decksData, setDecksData] = React.useState(null);
  const [fetchError, setFetchError] = React.useState(false);

  const fetchDecksData = async (showLoading) => {
    setIsLoading(showLoading);
    setFetchError(false);
    let api_params = {
      company_id: props.homeCompany,
    };
    try {
      const res = await getRightworkAPI(API_get_learning_swipe_deck_summaries, api_params);
      if (res.status === 200) {
        setDecksData(res.data.redux?.payload?.learning_swipe_decks || []);
      }
    } catch (error) {
      // Always clear the spinner — without this, a transient API/network
      // failure leaves the modal stuck on the loader and the user can
      // only recover by closing and reopening. Surface a toast and
      // expose a Retry button so they can recover in place. Don't
      // rethrow — the effect caller doesn't await this and a rejected
      // promise from there is an unhandled-rejection warning.
      setFetchError(true);
      toast.error("Couldn't load swipe decks. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (props.open) {
      // Reset selection on each open so a previously-picked deck doesn't
      // sneak through if the admin cancels and reopens.
      setSelectedDeck("");
      fetchDecksData(true);
    }
  }, [props.open]);

  return (
    <Modal
      open={props.open}
      onClose={() => props.handleClose()}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <Box sx={style}>
        <div className="absolute right-2 top-2">
          <IconButton
            className="text-gray-400 hover:text-gray-600"
            onClick={() => props.handleClose()}
            size="small"
            sx={{ borderRadius: "8px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        {!isLoading ? (
          <div>
            <div className="mb-6 flex items-center space-x-3">
              <StyleOutlinedIcon className="text-violet-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                Add Swipe Deck to Section
              </h3>
            </div>
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Swipe Deck
              </label>
              <p className="text-gray-600 text-sm mb-3">
                Select from the swipe decks you have created to add to this section.
              </p>
              <div className="flex-1">
                <div className="w-full">
                  <Select
                    fullWidth
                    value={selectedDeck}
                    displayEmpty
                    onChange={(e) => handleDropdownDeckSelect(e.target.value)}
                    size="small"
                    sx={{
                      borderRadius: "10px",
                      paddingY: "5px",
                      height: "45px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    <MenuItem disabled value="">
                      Select a swipe deck
                    </MenuItem>
                    {(decksData || []).map((deck, index) => (
                      <MenuItem key={index} value={deck}>
                        {deck.name || "Untitled deck"}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              {fetchError && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
                  <p className="text-sm text-rose-700">
                    Couldn't load swipe decks.
                  </p>
                  <button
                    type="button"
                    onClick={() => fetchDecksData(true)}
                    className="text-sm font-medium text-rose-700 hover:text-rose-900 underline"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!fetchError && decksData && decksData.length === 0 && (
                <p className="mt-3 text-sm text-gray-500">
                  No swipe decks yet. Create one in the Swipe Decks tab first.
                </p>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={() => props.handleClose()}
              >
                Cancel
              </button>
              {selectedDeck ? (
                <button
                  className="flex-1 px-4 py-3 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors duration-200 text-sm font-medium"
                  onClick={() => {
                    props.handleSwipeDeckSelect(selectedDeck);
                    props.handleClose();
                  }}
                >
                  Add Swipe Deck
                </button>
              ) : (
                <button
                  className="flex-1 px-4 py-3 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed text-sm font-medium"
                >
                  Add Swipe Deck
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full h-[400px]">
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <LoadSpinner />
            </div>
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default AddSwipeDeckToSectionModal;
